import React, { useState } from 'react';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  Input,
  Select,
  Row,
  Col,
  Typography,
  App,
  Alert,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, EditOutlined, EyeOutlined, PrinterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  purchaseReturnService,
  type PurchaseReturnDTO,
  type PurchaseReturnFilters,
} from '@/services/purchaseReturn.service';

const { Title, Text } = Typography;

const statusColors: Record<string, string> = {
  Draft: 'default',
  Submitted: 'blue',
  Approved: 'green',
  Posted: 'cyan',
  Cancelled: 'red',
};

export const PurchaseReturnsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [dateFrom, setDateFrom] = useState<string | undefined>(undefined);
  const [dateTo, setDateTo] = useState<string | undefined>(undefined);

  const filters: PurchaseReturnFilters = {
    status: statusFilter,
    date_from: dateFrom,
    date_to: dateTo,
  };

  const { data: list = [], isLoading } = useQuery({
    queryKey: ['purchase-returns', filters],
    queryFn: () => purchaseReturnService.list(filters),
  });

  const handleSubmit = async (id: string) => {
    try {
      await purchaseReturnService.submit(id);
      message.success('Purchase return submitted');
      queryClient.invalidateQueries({ queryKey: ['purchase-returns'] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to submit');
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await purchaseReturnService.approve(id);
      message.success('Purchase return approved');
      queryClient.invalidateQueries({ queryKey: ['purchase-returns'] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to approve');
    }
  };

  const handlePost = async (id: string) => {
    try {
      await purchaseReturnService.post(id);
      message.success('Purchase return posted');
      queryClient.invalidateQueries({ queryKey: ['purchase-returns'] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to post');
    }
  };

  const handleCancel = async (id: string) => {
    try {
      await purchaseReturnService.cancel(id);
      message.success('Purchase return cancelled');
      queryClient.invalidateQueries({ queryKey: ['purchase-returns'] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to cancel');
    }
  };

  const columns: ColumnsType<PurchaseReturnDTO> = [
    {
      title: 'Return #',
      dataIndex: 'return_number',
      key: 'return_number',
      fixed: 'left',
      width: 130,
      render: (val: string, record: PurchaseReturnDTO) => (
        <a onClick={() => navigate(`/scm/procurement/purchase-returns/${record.id}`)}>{val ?? '—'}</a>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'return_date',
      key: 'return_date',
      width: 110,
      render: (d) => (d ? dayjs(d).format('DD-MMM-YYYY') : '—'),
    },
    {
      title: 'Supplier',
      key: 'supplier',
      render: (_, r) => r.supplier?.supplier_name ?? r.supplier_id ?? '—',
    },
    {
      title: 'GRN',
      key: 'grn',
      width: 120,
      render: (_, r) => r.goodsReceiptNote?.grn_number ?? r.grn_id ?? '—',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s: string) => <Tag color={statusColors[s] ?? 'default'}>{s}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 260,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small" wrap>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/scm/procurement/purchase-returns/${record.id}`)}>
            View
          </Button>
          {record.status === 'Draft' && (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => navigate(`/scm/procurement/purchase-returns/${record.id}/edit`)}
            >
              Edit
            </Button>
          )}
          {record.status === 'Draft' && (
            <Button type="link" size="small" onClick={() => handleSubmit(record.id)}>
              Submit
            </Button>
          )}
          {record.status === 'Submitted' && (
            <Button type="link" size="small" onClick={() => handleApprove(record.id)}>
              Approve
            </Button>
          )}
          {(record.status === 'Submitted' || record.status === 'Approved') && (
            <Button type="link" size="small" onClick={() => handlePost(record.id)}>
              Post
            </Button>
          )}
          {!['Posted', 'Cancelled'].includes(record.status) && (
            <Button type="link" size="small" danger onClick={() => handleCancel(record.id)}>
              Cancel
            </Button>
          )}
          <Button type="link" size="small" icon={<PrinterOutlined />} onClick={() => navigate(`/scm/procurement/purchase-returns/${record.id}`)}>
            Print
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col flex="auto">
          <Title level={4} style={{ margin: 0 }}>
            Purchase Returns
          </Title>
          <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
            Create and manage purchase returns against GRN; post to reverse stock and create debit notes
          </Text>
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 16 }}
            message="Purchase return register"
            description="Filter by status and dates. Open a return # for edit, workflow, and print. Sticky header while scrolling."
          />
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/scm/procurement/purchase-returns/new')}>
            Create Purchase Return
          </Button>
        </Col>
      </Row>

      <Card>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Status"
                allowClear
                style={{ width: '100%' }}
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: 'Draft', label: 'Draft' },
                  { value: 'Submitted', label: 'Submitted' },
                  { value: 'Approved', label: 'Approved' },
                  { value: 'Posted', label: 'Posted' },
                  { value: 'Cancelled', label: 'Cancelled' },
                ]}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Input
                type="date"
                placeholder="From date"
                value={dateFrom ?? ''}
                onChange={(e) => setDateFrom(e.target.value || undefined)}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Input
                type="date"
                placeholder="To date"
                value={dateTo ?? ''}
                onChange={(e) => setDateTo(e.target.value || undefined)}
                style={{ width: '100%' }}
              />
            </Col>
          </Row>

          <Table
            loading={isLoading}
            columns={columns}
            dataSource={list.map((o) => ({ ...o, key: o.id }))}
            sticky={{ offsetHeader: 0 }}
            pagination={{
              defaultPageSize: 20,
              showSizeChanger: true,
              showTotal: (t) => `Total ${t} returns`,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            size="small"
            scroll={{ x: 1000, y: 520 }}
          />
        </Space>
      </Card>
    </div>
  );
};
